// @givmo/board — one backfill batch: what came in under it, and drawing the line under it.
//
// ══ THE THREE STATES THIS PAGE HAS TO MAKE HONEST ══════════════════════════════════════════════
//
//   1. OPEN — records can be entered, and the batch's own label and description can still be edited.
//   2. ALREADY IN THE BOOK — a replayed entry is answered with the record that was already there,
//      `already_recorded: true`, HTTP 200 instead of 201. The reachable case is a retry, not a
//      mistake, so it renders as a plain statement of what happened with the record named. A red
//      error here would send an officer hunting a problem that does not exist.
//   3. CLOSED — terminal. Nothing further enters, and the batch's own description freezes with it.
//      The remedy the backend names is a NEW batch, never re-opening this one: the boundary was drawn
//      around a corpus, and moving it after records were filed against it would rewrite what those
//      records say about where they came from. A database trigger refuses the same two writes
//      underneath, so a path that never asked the service cannot move it either.
//
// The closing act is recorded ON THE ROW — who closed it, when, and their note — rather than in a log,
// because two states and one transition mean the row IS the record of who drew the boundary. Closing
// is also idempotent and does NOT overwrite the first closure, so this page never offers to "re-close"
// and never presents the closure as something that could be amended.
//
// ⚠ THE RECORD COUNT IS READER-SCOPED AND IS NEVER COMPARED AGAINST ANYTHING. It counts what THIS
// reader may see; a stored count could not be scoped at all, and an unscoped one would disclose
// withheld records by arithmetic. So this page shows the count and the rows and stops — no "n of m",
// no completeness figure, no note about anything not shown.
//
// CBG-AI5-PROPOSE (frame b6; backend `board-ai-proposals.md` §6.2): a batch of past records the
// organization's connected AI proposed is confirmed into one of these, closed in the same act with no
// closing note (Board writes none in the organization's place). Its closed card says who proposed
// what it holds instead, from the batch's own fields (the proposed batch's page is its own route,
// `BOARD_PAGES.proposedBatch`).
//
// CBG-UX11 (design-284 §2.5, §3): the batches are dark behind the backend's
// `BOARD_IMPORT_BATCHES_ENABLED`, off everywhere. While it is off this page's read answers the
// absent-route 404, and the page replaces itself with the import page, so an old link to a batch
// lands there. Turned on, the page serves as built.

import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Fingerprint } from '../../components/fingerprint';
import { Link, useNavigate, useParams } from '../../board-routing';
import { isGivmoError, type OrgBoardImportBatchOut } from '@givmo/api-client';
import {
  Badge,
  Button,
  Card,
  CardBody,
  Dialog,
  ErrorMessage,
  Skeleton,
  TextField,
  Textarea,
  toast,
} from '@givmo/design-system';
import { PageShell } from '../../components/page-shell';
import { FieldNote } from '../../components/field-note';
import { useBoardHost } from '../../board-host';
import { hasBoardPermission } from '../../board-host';
import { BOARD_PAGES } from '../../board-routes';
import { displayDate, displayInstant, displayLongDate } from '../../lib/display';
import { isNotServed } from '../../lib/not-served';
import { recordKindLabel } from '../records/records-decode';
import { ImportedRecordDialog } from './imported-record-dialog';
import { importBatchesQueryKey } from './import-batches-page';
import {
  batchCloserSuffix,
  batchStatusLabel,
  batchStatusTone,
  extractBatchRecords,
  importOutcomeLine,
  isOpenBatch,
  recordCount,
  type ImportedRecordSummary,
} from './import-decode';
import { useBoardOperations } from '../../board-runtime';
// CBG-AI5-PROPOSE (frame b6): the line a confirmed batch carries.
import { proposedThrough } from '../proposals/proposal-decode';
import { proposalWords as pw } from '../proposals/proposal-words';

/** Never retry the route miss: it is an answer, not a blip. */
function retryUnlessNotServed(failureCount: number, error: unknown): boolean {
  return !isNotServed(error) && failureCount < 1;
}

export function ImportBatchPage() {
  const operations = useBoardOperations();
  const { batchId } = useParams({ from: '/authenticated/import-batches/$batchId' });
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { permissions, orgRef, isReady, userId } = useBoardHost();
  const canImport = hasBoardPermission(permissions, 'board_record:import');

  const [entering, setEntering] = useState(false);
  const [editing, setEditing] = useState(false);
  const [closing, setClosing] = useState(false);
  const [outcome, setOutcome] = useState<{ line: string; recordId: string } | null>(null);

  const batchKey = ['board-import-batch', orgRef, batchId] as const;
  const recordsKey = ['board-import-batch-records', orgRef, batchId] as const;

  const batchQuery = useQuery({
    queryKey: batchKey,
    enabled: isReady && orgRef !== null,
    retry: retryUnlessNotServed,
    queryFn: async (): Promise<OrgBoardImportBatchOut> => {
      const { data } = await operations.officerGetBoardImportBatch({
        path: { org_ref: orgRef ?? '', batch_id: batchId },
        throwOnError: true,
      });
      return data;
    },
  });

  const recordsQuery = useQuery({
    queryKey: recordsKey,
    enabled: isReady && orgRef !== null,
    retry: retryUnlessNotServed,
    queryFn: async (): Promise<ImportedRecordSummary[]> => {
      const { data } = await operations.officerListBoardImportBatchRecords({
        path: { org_ref: orgRef ?? '', batch_id: batchId },
        query: { 'page[limit]': 100 },
        throwOnError: true,
      });
      return extractBatchRecords(data);
    },
  });

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: batchKey });
    await queryClient.invalidateQueries({ queryKey: recordsKey });
    await queryClient.invalidateQueries({ queryKey: importBatchesQueryKey(orgRef) });
  };

  const batch = batchQuery.data ?? null;

  // The batches are not served here: the import page takes over (design-284 §2.5).
  const notServed = batchQuery.isError && isNotServed(batchQuery.error);
  useEffect(() => {
    if (notServed) void navigate({ to: BOARD_PAGES.importBatches, replace: true });
  }, [notServed, navigate]);

  if (batchQuery.isPending || notServed) {
    return (
      <PageShell title="Batch" testId="page-import-batch">
        <Skeleton className="h-90 w-full rounded-lg" testId="import-batch-loading" />
      </PageShell>
    );
  }

  if (batchQuery.isError || batch === null) {
    return (
      <PageShell title="Batch" testId="page-import-batch">
        <Card testId="import-batch-detail-error">
          <CardBody className="flex flex-col items-start gap-12">
            <p className="font-sans text-s15 font-w400 leading-h15 tracking-givmo text-text-secondary">
              We couldn’t open this batch.
            </p>
            <Button
              appearance="saas"
              tone="secondary"
              onClick={() => void batchQuery.refetch()}
              testId="import-batch-detail-retry"
            >
              Try again
            </Button>
          </CardBody>
        </Card>
      </PageShell>
    );
  }

  const open = isOpenBatch(batch);
  const records = recordsQuery.data ?? [];
  const org = orgRef ?? '';

  return (
    <PageShell
      title={batch.label}
      description={batch.source_description}
      actions={
        <Link
          to="/import-batches"
          // Exact: this page sits beneath that address, and it is not the current page.
          exact
          className="font-sans text-s14 font-w600 tracking-givmo text-brand-600 outline-none transition-colors hover:text-brand-700 focus-visible:underline"
          data-testid="import-batch-back"
        >
          All batches
        </Link>
      }
      testId="page-import-batch"
    >
      <div className="flex flex-wrap items-center gap-8">
        <Badge tone={batchStatusTone(batch.status)}>{batchStatusLabel(batch.status)}</Badge>
        <Badge tone="neutral">
          {recordCount(batch)} {recordCount(batch) === 1 ? 'record' : 'records'}
        </Badge>
        <span className="font-sans text-s12 font-w400 tracking-givmo text-text-tertiary">
          Started {displayDate(batch.created_at)}
        </span>
      </div>

      {!open ? (
        <Card testId="import-batch-closed">
          <CardBody className="flex flex-col gap-6">
            <span className="font-sans text-s15 font-w600 tracking-givmo text-text-strong">
              This batch is closed
            </span>
            <span className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary">
              {canImport
                ? 'Nothing further goes in, and its description can’t be changed. If there is more to import, start another batch — the line you drew around this one stays where you drew it.'
                : 'Nothing further goes in, and its description can’t be changed.'}
            </span>
            <span className="font-sans text-s12 font-w400 tracking-givmo text-text-tertiary">
              Closed {displayInstant(batch.closed_at)}
              {batchCloserSuffix(batch)}
            </span>
            {/* CBG-AI5-PROPOSE (frame b6): who proposed what this batch holds. */}
            {batch.proposed_at ? (
              <span
                className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary"
                data-testid="import-batch-proposed-from"
              >
                {pw.importedFromLine(
                  proposedThrough(batch, userId),
                  displayLongDate(batch.proposed_at) ?? '—',
                )}
              </span>
            ) : null}
            {batch.closure_note ? (
              <span
                className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary"
                data-testid="import-batch-closure-note"
              >
                {batch.closure_note}
              </span>
            ) : null}
          </CardBody>
        </Card>
      ) : null}

      {/* Every act on a batch is `board_record:import`: a reader sees the batch and what is in it. */}
      {open && canImport ? (
        <div className="flex flex-wrap gap-10">
          <Button
            appearance="saas"
            tone="primary"
            onClick={() => setEntering(true)}
            testId="import-batch-add"
          >
            Record something from before
          </Button>
          <Button
            appearance="saas"
            tone="secondary"
            onClick={() => setEditing(true)}
            testId="import-batch-edit"
          >
            Edit this batch
          </Button>
          <Button
            appearance="saas"
            tone="secondary"
            onClick={() => setClosing(true)}
            testId="import-batch-close"
          >
            Close this batch
          </Button>
        </div>
      ) : null}

      {/* The already-in-the-book state. Rendered as a STATEMENT, never as an error — a replay is a
          retry, and the record it names is the one that was already there. */}
      {outcome !== null ? (
        <Card testId="import-batch-outcome">
          <CardBody className="flex flex-col items-start gap-8">
            <span className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary">
              {outcome.line}
            </span>
            <Link
              to="/records/$recordId"
              params={{ recordId: outcome.recordId }}
              className="font-sans text-s13 font-w600 tracking-givmo text-brand-600 outline-none transition-colors hover:text-brand-700 focus-visible:underline"
              data-testid="import-batch-outcome-link"
            >
              Open the record
            </Link>
          </CardBody>
        </Card>
      ) : null}

      {/* ── What came in ───────────────────────────────────────────────────────────────────── */}
      <Card testId="import-batch-records">
        <CardBody className="flex flex-col gap-12">
          <h2 className="font-sans text-s15 font-w600 tracking-givmo text-text-strong">
            What came in under this batch
          </h2>

          {recordsQuery.isPending ? (
            <Skeleton
              className="h-[60px] w-full rounded-lg"
              testId="import-batch-records-loading"
            />
          ) : null}

          {recordsQuery.isSuccess && records.length === 0 ? (
            <p
              className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary"
              data-testid="import-batch-records-empty"
            >
              Nothing entered yet.
            </p>
          ) : null}

          <ul className="flex flex-col gap-12">
            {records.map((record) => (
              <li
                key={record.id}
                className="flex flex-col gap-4 border-t border-border-subtle pt-12 first:border-t-0 first:pt-0"
                data-testid={`import-batch-record-${record.id}`}
              >
                <div className="flex flex-wrap items-start justify-between gap-10">
                  <Link
                    to="/records/$recordId"
                    params={{ recordId: record.id }}
                    className="min-w-0 font-sans text-s14 font-w600 tracking-givmo text-text-strong outline-none transition-colors hover:text-brand-700 focus-visible:underline"
                    data-testid={`import-batch-record-link-${record.id}`}
                  >
                    {record.title ?? 'Untitled'}
                  </Link>
                  <Badge tone="neutral">{recordKindLabel(record.record_kind)}</Badge>
                </div>
                <span className="font-sans text-s13 font-w400 tracking-givmo text-text-tertiary">
                  {record.occurred_on
                    ? `About ${displayDate(record.occurred_on)}`
                    : 'No date recorded'}
                  {record.created_at ? ` · entered ${displayDate(record.created_at)}` : ''}
                </span>
                {record.imported_source_note ? (
                  <span className="font-sans text-s12 font-w400 leading-h15 tracking-givmo text-text-tertiary">
                    {record.imported_source_note}
                  </span>
                ) : null}
                {record.content_hash ? (
                  // The WHOLE fingerprint (CBG-UX4): a shortened one cannot be compared.
                  <div className="flex flex-col gap-4 pt-2">
                    <span className="font-sans text-s12 font-w400 leading-h15 tracking-givmo text-text-tertiary">
                      Its fingerprint
                    </span>
                    <Fingerprint
                      value={record.content_hash}
                      testId={`import-record-hash-${record.id}`}
                      label={record.title}
                    />
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        </CardBody>
      </Card>

      {entering ? (
        <ImportedRecordDialog
          orgRef={org}
          batchId={batchId}
          onClose={() => setEntering(false)}
          onEntered={(record) => {
            setEntering(false);
            setOutcome({ line: importOutcomeLine(record), recordId: record.id });
            void refresh();
          }}
        />
      ) : null}

      {editing ? (
        <EditBatchDialog
          orgRef={org}
          batchId={batchId}
          label={batch.label}
          sourceDescription={batch.source_description}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            void refresh();
          }}
        />
      ) : null}

      {closing ? (
        <CloseBatchDialog
          orgRef={org}
          batchId={batchId}
          onClose={() => setClosing(false)}
          onClosed={() => {
            setClosing(false);
            void refresh();
          }}
        />
      ) : null}
    </PageShell>
  );
}

/** Editing the batch's own description. Refused once closed — the refusal is rendered verbatim. */
function EditBatchDialog({
  orgRef,
  batchId,
  label: initialLabel,
  sourceDescription: initialDescription,
  onClose,
  onSaved,
}: {
  orgRef: string;
  batchId: string;
  label: string;
  sourceDescription: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const operations = useBoardOperations();
  const [label, setLabel] = useState(initialLabel);
  const [sourceDescription, setSourceDescription] = useState(initialDescription);
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: async () => {
      await operations.officerUpdateBoardImportBatch({
        path: { org_ref: orgRef, batch_id: batchId },
        body: { label: label.trim(), source_description: sourceDescription.trim() },
        throwOnError: true,
      });
    },
    onSuccess: () => {
      toast.success('Saved.');
      onSaved();
    },
    onError: (caught) =>
      setError(isGivmoError(caught) ? caught.message : 'We couldn’t save that. Please try again.'),
  });

  return (
    <Dialog.Root open onOpenChange={(next) => !next && !save.isPending && onClose()}>
      <Dialog.Content
        aria-label="Edit this batch"
        className="flex flex-col gap-16"
        testId="import-batch-edit-dialog"
      >
        <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
          Edit this batch
        </Dialog.Title>

        <TextField
          label="What to call it"
          value={label}
          maxLength={200}
          onChange={(event) => setLabel(event.target.value)}
          testId="import-batch-edit-label"
        />
        <Textarea
          label="Where these records came from"
          rows={4}
          value={sourceDescription}
          onChange={(event) => setSourceDescription(event.target.value)}
          testId="import-batch-edit-source"
        />
        <FieldNote>You can change this while the batch is open, and not after.</FieldNote>

        {error !== null ? (
          <ErrorMessage testId="import-batch-edit-error">{error}</ErrorMessage>
        ) : null}

        <div className="flex justify-end gap-10">
          <Button
            appearance="saas"
            tone="secondary"
            disabled={save.isPending}
            onClick={onClose}
            testId="import-batch-edit-cancel"
          >
            Cancel
          </Button>
          <Button
            appearance="saas"
            tone="primary"
            loading={save.isPending}
            disabled={
              label.trim().length === 0 || sourceDescription.trim().length === 0 || save.isPending
            }
            onClick={() => {
              setError(null);
              save.mutate();
            }}
            testId="import-batch-edit-save"
          >
            Save
          </Button>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
}

/**
 * Closing a batch.
 *
 * The confirmation is not ceremony: closing is TERMINAL and there is no re-open, at the service or in
 * the database. So the dialog states the consequence in the sentence the officer reads before the
 * click, rather than leaving them to discover it from a refusal afterwards.
 */
function CloseBatchDialog({
  orgRef,
  batchId,
  onClose,
  onClosed,
}: {
  orgRef: string;
  batchId: string;
  onClose: () => void;
  onClosed: () => void;
}) {
  const operations = useBoardOperations();
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  const close = useMutation({
    mutationFn: async () => {
      await operations.officerCloseBoardImportBatch({
        path: { org_ref: orgRef, batch_id: batchId },
        body: note.trim().length > 0 ? { note: note.trim() } : {},
        throwOnError: true,
      });
    },
    onSuccess: () => {
      toast.success('Batch closed.');
      onClosed();
    },
    onError: (caught) =>
      setError(isGivmoError(caught) ? caught.message : 'We couldn’t close that. Please try again.'),
  });

  return (
    <Dialog.Root open onOpenChange={(next) => !next && !close.isPending && onClose()}>
      <Dialog.Content
        aria-label="Close this batch"
        className="flex flex-col gap-16"
        testId="import-batch-close-dialog"
      >
        <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
          Close this batch
        </Dialog.Title>

        <p className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary">
          Nothing more goes in after this, and the batch’s description can’t be changed. There is no
          re-opening it — if more turns up, start another batch. What is already in your minute book
          stays exactly as it is.
        </p>

        <Textarea
          label="A note about closing it (optional)"
          rows={3}
          value={note}
          maxLength={2000}
          onChange={(event) => setNote(event.target.value)}
          testId="import-batch-close-note"
        />
        <FieldNote>
          Kept with the batch, alongside who closed it and when — “everything in the binder is now
          in”.
        </FieldNote>

        {error !== null ? (
          <ErrorMessage testId="import-batch-close-error">{error}</ErrorMessage>
        ) : null}

        <div className="flex justify-end gap-10">
          <Button
            appearance="saas"
            tone="secondary"
            disabled={close.isPending}
            onClick={onClose}
            testId="import-batch-close-cancel"
          >
            Cancel
          </Button>
          <Button
            appearance="saas"
            tone="danger"
            loading={close.isPending}
            disabled={close.isPending}
            onClick={() => {
              setError(null);
              close.mutate();
            }}
            testId="import-batch-close-confirm"
          >
            Close it
          </Button>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
}
